import { useState } from "react";
import { AudioLines, RotateCcw, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { Switch } from "@/components/ui/switch";
import { usePlayer } from "@/context/PlayerContext";
import { BANDS, GAIN_RANGE, PRESETS, presetGains } from "@/lib/equalizer";

const fmtHz = (f) => (f >= 1000 ? `${f / 1000}k` : `${f}`);
const db = (v) => `${v > 0 ? "+" : ""}${Number(v).toFixed(v % 1 ? 1 : 0)} dB`;
const same = (a, b) => a && b && a.every((v, i) => v === b[i]);

const Band = ({ hz, value, onChange, disabled }) => (
  <div className="flex flex-col items-center gap-2" data-testid={`eq-band-${hz}`}>
    <span className="font-mono text-[10px] text-zinc-500" data-testid={`eq-band-${hz}-value`}>{db(value)}</span>
    <SliderPrimitive.Root orientation="vertical" value={[value]} min={-GAIN_RANGE} max={GAIN_RANGE} step={0.5} disabled={disabled} onValueChange={([v]) => onChange(v)}
      onDoubleClick={() => onChange(0)} className="relative flex h-36 w-5 touch-none select-none flex-col items-center" data-testid={`eq-band-${hz}-slider`}>
      <SliderPrimitive.Track className="relative h-full w-1.5 grow overflow-hidden rounded-full bg-zinc-200">
        <div className="absolute left-0 right-0 top-1/2 h-px bg-zinc-400" />
        <div className="absolute left-0 right-0 bg-zinc-900" style={value >= 0 ? { bottom: "50%", height: `${(value / GAIN_RANGE) * 50}%` } : { top: "50%", height: `${(-value / GAIN_RANGE) * 50}%` }} />
      </SliderPrimitive.Track>
      <SliderPrimitive.Thumb className="block h-4 w-4 rounded-full border border-zinc-400 bg-white shadow focus-visible:outline-none disabled:opacity-50" />
    </SliderPrimitive.Root>
    <span className="font-mono text-[10px] text-zinc-600">{fmtHz(hz)}</span>
  </div>
);

const Dial = ({ label, value, onChange, disabled, testid }) => (
  <label className="block text-[11px] text-zinc-500">
    <span className="flex justify-between"><span>{label}</span><span className="font-mono text-zinc-700" data-testid={`${testid}-value`}>{db(value)}</span></span>
    <Slider value={[value]} min={-GAIN_RANGE} max={GAIN_RANGE} step={0.5} disabled={disabled} onValueChange={([v]) => onChange(v)} className="mt-2" data-testid={testid} />
  </label>
);

export const EqualizerDialog = ({ open, onOpenChange }) => {
  const { eq, setEq } = usePlayer();
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const off = !eq.enabled;
  const names = [...Object.keys(PRESETS), ...Object.keys(eq.custom)];
  const active = names.find((n) => same(presetGains(eq, n), eq.gains)) || null;
  const pick = (n) => setEq({ preset: n, gains: presetGains(eq, n) });
  const setBand = (i, v) => setEq((e) => ({ preset: "Custom", gains: e.gains.map((g, j) => (j === i ? v : g)) }));
  const saveCustom = () => {
    const n = name.trim();
    if (!n) return;
    if (PRESETS[n]) { toast.error("That name is a built-in preset"); return; }
    setEq((e) => ({ preset: n, custom: { ...e.custom, [n]: e.gains } }));
    toast.success(`Saved preset "${n}"`);
    setSaving(false); setName("");
  };
  const removeCustom = (n) => setEq((e) => { const c = { ...e.custom }; delete c[n]; return { custom: c, preset: e.preset === n ? "Custom" : e.preset }; });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-2rem)] max-w-2xl overflow-x-hidden overflow-y-auto" data-testid="eq-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3 font-heading text-2xl">
            <span className="flex items-center gap-2"><AudioLines size={18} className="text-amber-600" />Equalizer</span>
            <label className="flex items-center gap-2 text-sm font-normal text-zinc-600"><Switch checked={eq.enabled} onCheckedChange={(v) => setEq({ enabled: v })} data-testid="eq-enabled-switch" />{eq.enabled ? "On" : "Off"}</label>
          </DialogTitle>
        </DialogHeader>
        <p className="-mt-2 text-xs text-zinc-500">Shapes everything you play on this device — streamed, offline and radio. Settings are saved in this browser.</p>
        <div className="flex flex-wrap gap-1.5" data-testid="eq-presets">
          {names.map((n) => (
            <span key={n} className="group relative">
              <button type="button" onClick={() => pick(n)} disabled={off} data-testid={`eq-preset-${n.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`rounded-full border px-3 py-1 text-xs transition-colors disabled:opacity-40 ${active === n ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-900"} ${eq.custom[n] ? "pr-7" : ""}`}>{n}</button>
              {eq.custom[n] && <button type="button" onClick={() => removeCustom(n)} title="Delete preset" data-testid={`eq-preset-delete-${n.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className={`absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full p-0.5 ${active === n ? "text-zinc-300 hover:text-white" : "text-zinc-400 hover:text-red-600"}`}><Trash2 size={10} /></button>}
            </span>
          ))}
          {!active && <span className="rounded-full border border-dashed border-amber-300 bg-amber-50 px-3 py-1 text-xs text-amber-800" data-testid="eq-preset-custom">Custom</span>}
        </div>
        <div className={`grid grid-cols-10 gap-1 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-4 ${off ? "opacity-50" : ""}`} data-testid="eq-bands">
          {BANDS.map((hz, i) => <Band key={hz} hz={hz} value={eq.gains[i]} disabled={off} onChange={(v) => setBand(i, v)} />)}
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <Dial label="Bass boost" value={eq.bass} disabled={off} onChange={(v) => setEq({ bass: v })} testid="eq-bass" />
          <Dial label="Treble boost" value={eq.treble} disabled={off} onChange={(v) => setEq({ treble: v })} testid="eq-treble" />
          <Dial label="Preamp" value={eq.preamp} disabled={off} onChange={(v) => setEq({ preamp: v })} testid="eq-preamp" />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-zinc-100 pt-4">
          <button type="button" onClick={() => setEq({ preset: "Flat", gains: PRESETS.Flat, bass: 0, treble: 0, preamp: 0 })} data-testid="eq-reset-button" className="flex items-center gap-2 rounded-full border border-zinc-300 px-4 py-1.5 text-xs hover:border-zinc-900"><RotateCcw size={12} />Reset to flat</button>
          {saving ? (
            <form onSubmit={(e) => { e.preventDefault(); saveCustom(); }} className="flex items-center gap-2">
              <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Preset name" maxLength={24} className="h-8 rounded-md border border-zinc-300 px-2 text-sm outline-none focus:border-zinc-900" data-testid="eq-save-name-input" />
              <button type="submit" disabled={!name.trim()} data-testid="eq-save-confirm" className="rounded-full bg-zinc-900 px-4 py-1.5 text-xs text-white disabled:opacity-40">Save</button>
              <button type="button" onClick={() => setSaving(false)} className="text-xs text-zinc-500 hover:text-zinc-900">Cancel</button>
            </form>
          ) : (
            <button type="button" onClick={() => setSaving(true)} disabled={off} data-testid="eq-save-button" className="flex items-center gap-2 rounded-full bg-zinc-900 px-4 py-1.5 text-xs text-white disabled:opacity-40"><Save size={12} />Save as my preset</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
